// GM Tool store: MongoDB-backed persistence via /api/admin/gm/sync,
// with localStorage cache for offline/instant reads. All upserts push
// to backend so the game engine reads the same data as the GM tool.

import { api } from "@/lib/api";

const KEYS = {
  enemies: "gm_enemies_v1",
  items: "gm_items_v1",
  classes: "gm_classes_v1",
  characters: "gm_characters_v1",
  world_bosses: "gm_world_bosses_v1",
  abilities: "gm_abilities_v1",
};

const load = (k) => { try { return JSON.parse(localStorage.getItem(k)) || []; } catch { return []; } };
const save = (k, v) => localStorage.setItem(k, JSON.stringify(v));
const newId = () => (crypto?.randomUUID?.() || Math.random().toString(36).slice(2) + Date.now().toString(36));

// Debounced sync: batch multiple upserts within 400ms into one API call.
let _syncTimer = null;
function scheduleSync() {
  if (_syncTimer) clearTimeout(_syncTimer);
  _syncTimer = setTimeout(() => {
    _syncTimer = null;
    const classes = load(KEYS.classes);
    // CLASS_SKILLS are stored on the class itself and mirrored into the
    // generic GM ability collection so the combat engine can use them.
    const classAbilities = classes.flatMap(cls =>
      (cls.skills || []).map(skill => ({
        ...skill,
        id: skill.id || `${String(cls.name || "class").toLowerCase().replace(/[^a-z0-9]+/g, "_")}_${newId().slice(0, 8)}`,
        target_type: "class",
        class_ids: [cls.name],
        class_name: cls.name,
      }))
    );
    const independentAbilities = load(KEYS.abilities).filter(a => a?.source !== "class_compiler");
    const abilities = [...independentAbilities, ...classAbilities.map(a => ({ ...a, source: "class_compiler" }))];
    save(KEYS.abilities, abilities);
    const payload = {
      enemies: load(KEYS.enemies),
      items: load(KEYS.items),
      classes,
      world_bosses: load(KEYS.world_bosses),
      abilities,
    };
    api.gmSync(payload).catch(err => {
      // Silent — offline / not-admin. localStorage still holds the state.
      // eslint-disable-next-line no-console
      console.warn("GM sync failed:", err?.response?.status || err?.message);
    });
  }, 400);
}

/** Pull all GM data from server → localStorage. Called once on GM page mount. */

function nearestPresetByHp(row, presets, hpKeys = ["hp", "max_hp"]) {
  const hp = Number(hpKeys.map(k => row?.[k]).find(v => v != null) || 120);
  return presets.reduce((best, p) => Math.abs(Number(p.hp) - hp) < Math.abs(Number(best.hp) - hp) ? p : best, presets[0]);
}

function normalizePulledState(r) {
  const enemies = (r.enemies || []).map(e => ({
    ...e,
    stat_preset: e.stat_preset || nearestPresetByHp(e, STAT_PRESETS).key,
    ability_ids: e.ability_ids || [],
  }));
  const worldBosses = (r.world_bosses || []).map(b => ({
    ...b,
    stat_preset: b.stat_preset || nearestPresetByHp(b, STAT_PRESETS).key,
  }));
  const classes = (r.classes || []).map(c => {
    const source = c.stats_snapshot || {};
    const stats = {
      strength: Number(c.starting_stats?.strength ?? source.str ?? source.strength ?? 8),
      defense: Number(c.starting_stats?.defense ?? source.def ?? source.defense ?? 8),
      dexterity: Number(c.starting_stats?.dexterity ?? source.dex ?? source.dexterity ?? 8),
      intelligence: Number(c.starting_stats?.intelligence ?? source.int ?? source.intelligence ?? 8),
      charisma: Number(c.starting_stats?.charisma ?? source.cha ?? source.charisma ?? 8),
      concentration: Number(c.starting_stats?.concentration ?? source.con ?? source.concentration ?? 8),
    };
    const rawSkills = c.skills || c.class_skills || [];
    return {
      ...c,
      starting_stats: stats,
      max_hp: Number(c.max_hp ?? source.hp ?? 30),
      skills: rawSkills.map(s => ({
        id: s.id || "",
        name: s.name || "",
        unlock: Number(s.unlock ?? 1),
        cost_pool: s.cost_pool || "vigor",
        cost: Number(s.cost ?? 0),
        power: Number(s.power ?? 1),
        desc: s.desc || "",
      })),
      primary_stat: c.primary_stat || c.primary_stat_key || "",
      mage: !!c.mage,
      martial: !!c.martial,
      ability_ids: c.ability_ids || [],
    };
  });
  return { ...r, enemies, world_bosses: worldBosses, classes, abilities: r.abilities || [] };
}

export async function pullFromServer() {
  try {
    const r = normalizePulledState(await api.gmPull());
    if (r.enemies)      save(KEYS.enemies,      r.enemies);
    if (r.items)        save(KEYS.items,        r.items);
    if (r.classes)      save(KEYS.classes,      r.classes);
    if (r.world_bosses) save(KEYS.world_bosses, r.world_bosses);
    if (r.abilities) save(KEYS.abilities, r.abilities);
    return true;
  } catch (e) {
    return false;
  }
}

export const gmStore = {
  enemies: {
    list: () => load(KEYS.enemies),
    save: (all) => { save(KEYS.enemies, all); scheduleSync(); },
    upsert(e) { const all = load(KEYS.enemies); const i = all.findIndex(x => x.id === e.id); if (i >= 0) all[i] = e; else all.unshift(e); save(KEYS.enemies, all); scheduleSync(); return e; },
    remove: (id) => { save(KEYS.enemies, load(KEYS.enemies).filter(x => x.id !== id)); scheduleSync(); },
  },
  items: {
    list: () => load(KEYS.items),
    save: (all) => { save(KEYS.items, all); scheduleSync(); },
    upsert(e) { const all = load(KEYS.items); const i = all.findIndex(x => x.id === e.id); if (i >= 0) all[i] = e; else all.unshift(e); save(KEYS.items, all); scheduleSync(); return e; },
    remove: (id) => { save(KEYS.items, load(KEYS.items).filter(x => x.id !== id)); scheduleSync(); },
  },
  classes: {
    list: () => load(KEYS.classes),
    save: (all) => { save(KEYS.classes, all); scheduleSync(); },
    upsert(e) { const all = load(KEYS.classes); const i = all.findIndex(x => x.id === e.id); if (i >= 0) all[i] = e; else all.unshift(e); save(KEYS.classes, all); scheduleSync(); return e; },
    remove: (id) => { save(KEYS.classes, load(KEYS.classes).filter(x => x.id !== id)); scheduleSync(); },
  },
  characters: {
    list: () => load(KEYS.characters),
    save: (all) => save(KEYS.characters, all),
    upsert(e) { const all = load(KEYS.characters); const i = all.findIndex(x => x.id === e.id); if (i >= 0) all[i] = e; else all.unshift(e); save(KEYS.characters, all); return e; },
    remove: (id) => { save(KEYS.characters, load(KEYS.characters).filter(x => x.id !== id)); },
  },
  abilities: {
    list: () => load(KEYS.abilities),
    save: (all) => { save(KEYS.abilities, all); scheduleSync(); },
    upsert(e) { const all = load(KEYS.abilities); const i = all.findIndex(x => x.id === e.id); if (i >= 0) all[i] = e; else all.unshift(e); save(KEYS.abilities, all); scheduleSync(); return e; },
    remove: (id) => { save(KEYS.abilities, load(KEYS.abilities).filter(x => x.id !== id)); scheduleSync(); },
  },
  world_bosses: {
    list: () => load(KEYS.world_bosses),
    save: (all) => { save(KEYS.world_bosses, all); scheduleSync(); },
    upsert(b) {
      const all = load(KEYS.world_bosses);
      if (b.active_week) all.forEach(x => { if (x.id !== b.id) x.active_week = false; });
      const i = all.findIndex(x => x.id === b.id);
      if (i >= 0) all[i] = b; else all.unshift(b);
      save(KEYS.world_bosses, all);
      scheduleSync();
      return b;
    },
    setActive(id) {
      const all = load(KEYS.world_bosses);
      all.forEach(x => { x.active_week = (x.id === id); });
      save(KEYS.world_bosses, all);
      scheduleSync();
    },
    remove: (id) => { save(KEYS.world_bosses, load(KEYS.world_bosses).filter(x => x.id !== id)); scheduleSync(); },
  },
};

// ── Random utilities ──────────────────────────────────────────────
const rand = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const pickN = (arr, n) => { const out = []; const used = new Set(); while (out.length < Math.min(n, arr.length)) { const i = Math.floor(Math.random() * arr.length); if (!used.has(i)) { used.add(i); out.push(arr[i]); } } return out; };

// ── ENEMIES ────────────────────────────────────────────────────────
export const ENEMY_TYPES = ["Umanoide", "Bestia", "Non-morto", "Demone", "Costrutto", "Elementale", "Drago", "Aberrazione", "Folletto", "Melma"];
const ENEMY_NAMES = {
  "Umanoide": ["Bandito", "Predone", "Mercenario", "Culista", "Necromante rinnegato", "Cacciatore di taglie", "Assassino", "Signore della guerra"],
  "Bestia": ["Lupo grigio", "Orso ferale", "Cinghiale zannuto", "Grifone selvaggio", "Pantera d'ombra", "Ragno cavernicolo", "Serpente delle rune"],
  "Non-morto": ["Scheletro rugginoso", "Zombie della palude", "Spettro affamato", "Lich minore", "Ghoul dei sepolcri", "Wight infernale", "Mummia scarna"],
  "Demone": ["Imp fiammeggiante", "Diavoletto dispettoso", "Incubo alato", "Balor minore", "Demone del sangue", "Sussurrante"],
  "Costrutto": ["Golem di pietra", "Automa arrugginito", "Guardiano d'ottone", "Sentinella d'ossidiana", "Statua animata"],
  "Elementale": ["Elementale di fiamma", "Elementale della roccia", "Elementale del vento", "Elementale del ghiaccio", "Elementale del fango"],
  "Drago": ["Draghetto rosso", "Wyvern nero", "Drago cromatico giovane", "Serpe di draco", "Draghetto d'ombra"],
  "Aberrazione": ["Occhio fluttuante", "Mente-flagellatore", "Aberrazione tentacolare"],
  "Folletto": ["Goblin razziatore", "Hobgoblin capitano", "Coboldo minatore", "Sprite dispettoso", "Redcap"],
  "Melma": ["Melma verde", "Cubo gelatinoso", "Ooze corrosiva"],
};
const ENEMY_TITLES = ["l'Insidioso", "il Sanguinario", "l'Ombraluna", "il Divoratore", "l'Impuro", "l'Antico", "il Ferocesegreto", "dai Denti di Ferro", "delle Fauci Rosse", "il Malato"];
const RESIST_KEYS = ["Fuoco", "Ghiaccio", "Fulmine", "Veleno", "Necrotico", "Radiante", "Fisico", "Psichico"];
const ENEMY_ABILITIES = [
  "Colpo devastante", "Fendente in area", "Grido di guerra", "Ululato paralizzante", "Sputo velenoso",
  "Morso infetto", "Volo radente", "Camuffamento", "Rigenerazione lenta", "Frenzy",
  "Sguardo pietrificante", "Aura di paura", "Colpo drenante", "Scudo spinato", "Contrattacco",
];

function coherentEnemyStats(level) {
  const base = 4 + level * 2;
  return {
    hp: rand(base * 6, base * 10),
    mana: rand(base, base * 3),
    str: rand(6, 10) + Math.floor(level / 2),
    dex: rand(6, 10) + Math.floor(level / 3),
    con: rand(6, 10) + Math.floor(level / 2),
    int: rand(4, 12),
    cha: rand(4, 12),
    def: 5 + Math.floor(level / 2) + rand(0, 4),
    attack: 5 + level + rand(0, 4),
    speed: rand(20, 40),
    xp: level * rand(40, 120),
    gold: level * rand(5, 25),
  };
}

export function generateRandomEnemy(availableItems = []) {
  const level = rand(1, 30);
  const type = pick(ENEMY_TYPES);
  const baseName = pick(ENEMY_NAMES[type] || ["Creatura"]);
  const withTitle = Math.random() < 0.35;
  const name = withTitle ? `${baseName} ${pick(ENEMY_TITLES)}` : baseName;
  const stats = coherentEnemyStats(level);
  const resistances = pickN(RESIST_KEYS, rand(0, 3));
  const weaknesses = pickN(RESIST_KEYS.filter(r => !resistances.includes(r)), rand(0, 2));
  const abilities = pickN(ENEMY_ABILITIES, rand(1, 3));
  const drops = availableItems.length
    ? pickN(availableItems, rand(1, Math.min(3, availableItems.length))).map(it => ({
        itemId: it.id, chance: rand(5, 60),
      }))
    : [];
  const rank = level <= 5 ? "Trascurabile" : level <= 12 ? "Sfida" : level <= 20 ? "Elite" : level <= 27 ? "Boss" : "Leggenda";
  const spawnRate = level <= 5 ? rand(30, 50) : level <= 12 ? rand(15, 30) : level <= 20 ? rand(5, 15) : rand(1, 5);
  return {
    id: newId(),
    name, level, rank, type,
    stat_preset: level <= 5 ? "weak" : level <= 12 ? "standard" : level <= 20 ? "elite" : level <= 27 ? "boss" : "legendary",
    ...stats,
    resistances, weaknesses, abilities,
    lore: `${name} è ${type === "Non-morto" ? "un'entità che rifiuta il riposo" : "una minaccia in queste terre"}. Livello ${level}.`,
    drops,
    spawn_rate: spawnRate,
    image: "",
    notes: "",
    animations: ["idle-float"],
    createdAt: Date.now(),
  };
}


// ── STAT PRESETS ───────────────────────────────────────────────────
// GM-created entities choose a balanced, predefined stat profile instead of
// entering combat numbers manually. Class bonuses are the only editable
// numeric stat modifiers and are applied additively to the selected base.
export const STAT_PRESETS = [
  { key: "weak", label: "Creatura Debole", hp: 80, mana: 20, attack: 8, def: 8, speed: 25, str: 8, dex: 8, con: 8, int: 5, cha: 5, xp: 40, gold: 5 },
  { key: "standard", label: "Creatura Standard", hp: 120, mana: 30, attack: 12, def: 12, speed: 30, str: 10, dex: 10, con: 10, int: 6, cha: 6, xp: 80, gold: 10 },
  { key: "elite", label: "Creatura Elite", hp: 250, mana: 60, attack: 25, def: 20, speed: 35, str: 16, dex: 14, con: 16, int: 10, cha: 8, xp: 180, gold: 35 },
  { key: "miniboss", label: "Miniboss", hp: 750, mana: 120, attack: 40, def: 35, speed: 30, str: 24, dex: 18, con: 24, int: 14, cha: 10, xp: 500, gold: 100 },
  { key: "boss", label: "Boss", hp: 1500, mana: 220, attack: 65, def: 50, speed: 28, str: 32, dex: 22, con: 30, int: 20, cha: 14, xp: 1200, gold: 250 },
  { key: "legendary", label: "Boss Leggendario", hp: 3000, mana: 400, attack: 100, def: 75, speed: 25, str: 45, dex: 28, con: 40, int: 28, cha: 18, xp: 2500, gold: 500 },
  // Marker preset — when selected, the sync layer trusts the raw stat inputs
  // typed by the admin instead of overwriting them with a bucket.
  { key: "custom", label: "Personalizzato (statistiche libere)" },
];

// Canonical list of numeric stats an admin can edit when a "custom" preset is
// selected. Order matters (drives the UI layout).
export const CUSTOM_STAT_KEYS = ["hp", "mana", "attack", "def", "speed", "str", "dex", "con", "int", "cha", "xp", "gold"];

export const GM_STATS = ["strength", "defense", "dexterity", "intelligence", "charisma", "concentration"];

// ── ITEMS ──────────────────────────────────────────────────────────
export const ITEM_TYPES = ["Arma", "Armatura", "Consumabile", "Materiale", "Accessorio", "Chiave", "Pergamena", "Reagente"];
export const RARITIES = [
  { key: "Comune",       color: "#B0ADA5", mult: 1 },
  { key: "Non comune",   color: "#6E8B3D", mult: 2 },
  { key: "Raro",         color: "#2A4B7C", mult: 5 },
  { key: "Epico",        color: "#5D2F5F", mult: 12 },
  { key: "Leggendario",  color: "#E55A00", mult: 30 },
  { key: "Mitico",       color: "#E5C34A", mult: 80 },
];
const ITEM_NAMES = {
  "Arma": ["Spada corta", "Ascia bipenne", "Lancia rugginosa", "Arco lungo", "Pugnale a serpe", "Martello runico", "Falcione", "Bastone da mago"],
  "Armatura": ["Cotta di maglia", "Armatura di piastre", "Veste da monaco", "Cuoio borchiato", "Mantello del bosco"],
  "Consumabile": ["Pozione di vita", "Elisir di mana", "Antidoto", "Pergamena di fuga", "Pane secco", "Vino speziato"],
  "Materiale": ["Zanna di lupo", "Fibra di lino", "Cristallo grezzo", "Osso di ghoul", "Cuore di golem", "Piuma di grifone"],
  "Accessorio": ["Anello di rame", "Ciondolo del sole", "Bracciale spinato", "Cintura del gigante", "Cerchietto della saggezza"],
  "Chiave": ["Chiave arrugginita", "Chiave del sepolcro", "Chiave d'ossidiana", "Chiave dorata"],
  "Pergamena": ["Pergamena di dardo", "Pergamena di scudo", "Pergamena di velocità"],
  "Reagente": ["Zolfo giallo", "Radice di mandragora", "Occhio di tritone", "Polvere d'anima"],
};
const ITEM_SUFFIXES = ["della Notte", "del Vento", "dei Corvi", "del Sangue", "dell'Alba", "della Rovina", "delle Ceneri", "del Vespro"];
const ITEM_EFFECTS = [
  "+1 Forza", "+2 Destrezza", "+1 Costituzione", "+2 Intelligenza",
  "+5 HP max", "+10 HP max", "+5 Mana max", "+10% velocità",
  "Resistenza al fuoco", "Resistenza al ghiaccio", "Immunità al veleno",
  "+1d4 danno fuoco", "+1d4 danno necrotico", "Chance critico +5%",
  "Cura 1d6 HP", "Ripristina 10 mana", "Rimuove veleno", "Rimuove paura",
];

export function generateRandomItem() {
  const type = pick(ITEM_TYPES);
  const rarity = pick(RARITIES);
  const baseName = pick(ITEM_NAMES[type] || ["Oggetto"]);
  const withSuffix = ["Raro", "Epico", "Leggendario", "Mitico"].includes(rarity.key) || Math.random() < 0.3;
  const name = withSuffix ? `${baseName} ${pick(ITEM_SUFFIXES)}` : baseName;
  const effectsCount = { "Comune": 0, "Non comune": 1, "Raro": 2, "Epico": 3, "Leggendario": 4, "Mitico": 5 }[rarity.key];
  const effects = pickN(ITEM_EFFECTS, effectsCount);
  const baseValue = { "Arma": 30, "Armatura": 50, "Consumabile": 8, "Materiale": 4, "Accessorio": 25, "Chiave": 10, "Pergamena": 20, "Reagente": 6 }[type];
  const goldValue = Math.round(baseValue * rarity.mult * (0.8 + Math.random() * 0.6));
  const weight = ["Arma", "Armatura"].includes(type) ? +(1 + Math.random() * 8).toFixed(1) : +(0.1 + Math.random() * 1.5).toFixed(2);
  const stackable = ["Consumabile", "Materiale", "Reagente", "Pergamena"].includes(type);
  return {
    id: newId(),
    name, type, rarity: rarity.key,
    description: effects.length ? `${type} ${rarity.key.toLowerCase()} che concede: ${effects.join(", ")}.` : `Un ${type.toLowerCase()} comune.`,
    effects, gold_value: goldValue, weight,
    requirements: rarity.mult > 5 ? `Livello ${rand(5, 20)}` : "—",
    stackable, max_stack: stackable ? pick([10, 20, 50, 99]) : 1,
    image: "",
    notes: "",
    animations: ["idle-breathe"],
    createdAt: Date.now(),
  };
}

// ── CLASSES ────────────────────────────────────────────────────────
// The class compiler uses the exact fields consumed by backend/game_data.py.
// No random class generation or role/preset data is used here.
export const CLASS_GAME_DATA_STATS = [
  "strength", "defense", "dexterity", "intelligence", "charisma", "concentration",
];
export const CLASS_PRIMARY_STATS = [
  "strength", "defense", "dexterity", "intelligence", "charisma",
];
export const CLASS_RESOURCE_POOLS = ["mana", "vigor"];

// ── ABILITIES ─────────────────────────────────────────────────────
export const ABILITY_POOLS = ["mana", "vigor"];
export const ABILITY_TARGETS = ["class", "enemy", "both"];
export function generateRandomAbility() {
  const names = ["Ashen Cleave", "Grave Pulse", "Void Lance", "Blood Roar", "Frost Bind", "Shadow Rend"];
  const pool = pick(ABILITY_POOLS);
  return {
    id: newId(), name: pick(names), description: "A GM-created combat ability.",
    cost_pool: pool, cost: rand(4, 12), power: Number((1 + Math.random() * 2).toFixed(1)),
    unlock: rand(1, 20), target_type: pick(ABILITY_TARGETS),
    class_ids: [], enemy_ids: [], image: "", notes: "", createdAt: Date.now(),
  };
}

// ── CHARACTERS ─────────────────────────────────────────────────────
const FIRST_NAMES = ["Aldric", "Bryn", "Cassia", "Doran", "Elara", "Fenwick", "Gaius", "Halia", "Ivar", "Juno", "Kael", "Lyra", "Morrigan", "Nero", "Odell", "Pyra", "Quinn", "Rowan", "Sable", "Thorn"];
const ALIGNMENTS = ["Legale Buono", "Neutrale Buono", "Caotico Buono", "Legale Neutrale", "Neutrale", "Caotico Neutrale", "Legale Malvagio", "Neutrale Malvagio", "Caotico Malvagio"];
const BACKGROUNDS = [
  "Cresciuto in un villaggio distrutto da razziatori.",
  "Ex-mercenario che cerca redenzione.",
  "Nobile decaduto in fuga da un contratto.",
  "Studente di un ordine mistico caduto.",
  "Orfano cresciuto dalle rovine.",
];

export function generateRandomCharacter(availableClasses = [], availableItems = []) {
  const cls = availableClasses.length ? pick(availableClasses) : null;
  const level = rand(1, 20);
  const base = 8 + level;
  return {
    id: newId(),
    name: pick(FIRST_NAMES),
    class_id: cls?.id || null,
    class_name_snapshot: cls?.name || "",
    level, xp: level * rand(300, 500),
    str: rand(base - 2, base + 6),
    def: rand(base - 2, base + 6),
    dex: rand(base - 2, base + 6),
    con: rand(base - 2, base + 6),
    int: rand(base - 2, base + 6),
    cha: rand(base - 2, base + 6),
    hp: base * rand(8, 12),
    mana: base * rand(3, 8),
    inventory: pickN(availableItems, Math.min(availableItems.length, rand(0, 4))).map(i => i.id),
    equipment: {},
    abilities: cls?.base_abilities?.slice(0, 2) || [],
    background: pick(BACKGROUNDS),
    alignment: pick(ALIGNMENTS),
    image: "",
    notes: "",
    animations: ["idle-float"],
    createdAt: Date.now(),
  };
}

// ── ANIMATIONS CATALOG ─────────────────────────────────────────────
export const ANIMATIONS = [
  { key: "hover-stretch",  label: "Hover Stretch",  kind: "hover" },
  { key: "hover-compress", label: "Hover Compress", kind: "hover" },
  { key: "hover-shake",    label: "Hover Shake",    kind: "hover" },
  { key: "hover-glow",     label: "Hover Glow",     kind: "hover" },
  { key: "hover-rotate",   label: "Hover Rotate",   kind: "hover" },
  { key: "idle-float",     label: "Idle Float",     kind: "idle" },
  { key: "idle-pulse",     label: "Idle Pulse",     kind: "idle" },
  { key: "idle-breathe",   label: "Idle Breathe",   kind: "idle" },
  { key: "idle-wobble",    label: "Idle Wobble",    kind: "idle" },
];

export function animationClassNames(list) {
  if (!list || list.length === 0) return "gm-anim-idle-float";
  return list.map(a => `gm-anim-${a}`).join(" ");
}

export function getRarityColor(key) {
  return (RARITIES.find(r => r.key === key) || RARITIES[0]).color;
}

// ── WORLD BOSSES ───────────────────────────────────────────────────
export const WORLD_BOSS_TITLES = ["il Divoratore di Mondi", "l'Ombra dell'Abisso", "Distruttore delle Ere", "il Re Marcio", "l'Antico Colosso", "il Signore dei Sepolcri", "il Titano di Cenere", "il Sussurro Oscuro"];
const WB_BASE_NAMES = ["Volcanya", "Necrosk", "Umbros", "Vhal'gash", "Kraath", "Morthek", "Ashendur", "Silvarax"];
const WB_PHASES = [
  "Fase 1: Colpi pesanti singoli",
  "Fase 2: Attacchi ad area",
  "Fase 3: Enrage — velocità raddoppiata",
  "Fase 4: Invocazione minion",
  "Fase finale: Colpo apocalittico",
];
const WB_MECHANICS = [
  "Terrificante Presenza (paura in area)", "Sciame di corvi", "Piogge di meteore",
  "Onda d'urto (knockback)", "Aura di veleno", "Resurrezione dei caduti",
  "Divoratore di mana", "Riflette danno magico",
];

export function generateRandomWorldBoss() {
  const base = pick(WB_BASE_NAMES);
  const title = pick(WORLD_BOSS_TITLES);
  const level = rand(25, 50);
  const scale = 100 + level * 20;
  return {
    id: newId(),
    name: `${base}, ${title}`,
    stat_preset: "boss",
    level,
    hp: scale * rand(30, 60),
    max_hp: scale * rand(30, 60),
    attack: 15 + Math.floor(level / 2) + rand(0, 10),
    defense: 8 + Math.floor(level / 3) + rand(0, 5),
    element: pick(["Fuoco", "Ghiaccio", "Ombra", "Fulmine", "Necromantico", "Radiante"]),
    difficulty: pick(["Sfida", "Elite", "Boss", "Leggenda", "Mitico"]),
    phases: pickN(WB_PHASES, rand(3, 5)),
    mechanics: pickN(WB_MECHANICS, rand(2, 4)),
    reward_gold: level * rand(200, 500),
    reward_xp: level * rand(500, 1200),
    reward_loot: `1 drop garantito Leggendario + ${rand(2, 5)} Epici`,
    lore: `${base} risvegliato dopo secoli di sonno. Solo un'intera gilda ha speranza di abbatterlo.`,
    active_week: false,
    scheduled_from: "",
    scheduled_to: "",
    image: "",
    notes: "",
    animations: ["idle-breathe", "idle-pulse"],
    createdAt: Date.now(),
  };
}

// ── SEED FROM LIVE GAME ────────────────────────────────────────────
import { GAME_SEED_CLASSES, GAME_SEED_ELITES, GAME_SEED_ENEMIES, GAME_SEED_ITEMS } from "./gmSeed";

export function seedFromGame({ enemies = true, items = true, classes = true, world_bosses = true } = {}) {
  const report = { enemies: 0, items: 0, classes: 0, world_bosses: 0 };

  if (items) {
    const existing = gmStore.items.list();
    const existingNames = new Set(existing.map(x => x.name.toLowerCase()));
    for (const it of GAME_SEED_ITEMS) {
      if (existingNames.has(it.name.toLowerCase())) continue;
      gmStore.items.upsert({
        id: newId(), ...it, requirements: "—",
        image: "", notes: "seed from game_data.py",
        animations: ["idle-breathe"], createdAt: Date.now(),
      });
      report.items++;
    }
  }

  if (classes) {
    const existing = gmStore.classes.list();
    const existingNames = new Set(existing.map(x => x.name.toLowerCase()));
    for (const c of GAME_SEED_CLASSES) {
      if (existingNames.has(c.name.toLowerCase())) continue;
      gmStore.classes.upsert({
        id: newId(), ...c, notes: "seed from game_data.py",
        animations: ["idle-breathe"], createdAt: Date.now(),
      });
      report.classes++;
    }
  }

  if (enemies) {
    const existing = gmStore.enemies.list();
    const existingNames = new Set(existing.map(x => x.name.toLowerCase()));
    // Random-encounter enemies (with sprites)
    for (const e of GAME_SEED_ENEMIES) {
      if (existingNames.has(e.name.toLowerCase())) continue;
      const stats = coherentEnemyStats(e.level);
      gmStore.enemies.upsert({
        id: newId(),
        name: e.name, type: e.type, level: e.level,
        rank: e.level <= 5 ? "Trascurabile" : e.level <= 12 ? "Sfida" : "Elite",
        ...stats, hp: e.hp,
        resistances: [], weaknesses: [],
        abilities: [], lore: e.flavor || "",
        drops: [], spawn_rate: e.spawn_rate,
        image: e.image || "", notes: "seed: random encounter",
        animations: ["idle-float"], createdAt: Date.now(),
      });
      report.enemies++;
    }
    // Named elites
    for (const el of GAME_SEED_ELITES) {
      if (existingNames.has(el.name.toLowerCase())) continue;
      const stats = coherentEnemyStats(el.level);
      gmStore.enemies.upsert({
        id: newId(),
        name: el.name, type: "Umanoide", level: el.level,
        rank: el.level >= 15 ? "Boss" : "Elite",
        hp: el.hp, mana: stats.mana,
        str: stats.str, dex: stats.dex, con: stats.con,
        int: stats.int, cha: stats.cha,
        def: el.def, attack: el.atk, speed: 30,
        resistances: [], weaknesses: [],
        abilities: [`Guaranteed drop: ${el.drop}`],
        lore: el.flavor,
        drops: [], spawn_rate: 5,
        xp: el.xp, gold: el.gold,
        image: el.image || "", notes: "seed: named elite",
        animations: ["idle-float", "idle-pulse"], createdAt: Date.now(),
      });
      report.enemies++;
    }
  }

  if (world_bosses) {
    // Seed one starter world boss template
    const existing = gmStore.world_bosses.list();
    if (existing.length === 0) {
      const wb = generateRandomWorldBoss();
      wb.name = "Kraath, il Divoratore di Mondi";
      wb.active_week = true;
      wb.notes = "seed template — modificalo o creane altri";
      gmStore.world_bosses.upsert(wb);
      report.world_bosses = 1;
    }
  }

  return report;
}
